/**
 * Invoice Repository Implementation
 * Implements the InvoiceRepository interface
 * Coordinates between API calls and mappers
 */

import { InvoiceRepository } from "../../domain/repositories/InvoiceRepository";
import {
  InvoiceEntity,
  InvoicesResultEntity,
  InvoiceOperationResult,
  InvoiceDataEntity,
  InvoiceStatus,
  InvoiceListStatus,
  InvoiceReviewStatus,
  ManualInvoiceCreatedEntity,
} from "../../domain/entities/InvoiceEntity";
import {
  getCreatedInBackendSuccessMessage,
  getDeletedFromBackendSuccessMessage,
  getFailedCreateInBackendMessage,
  getFailedUpdateInBackendMessage,
  getStoredAccountingBackend,
  getUpdatedInBackendSuccessMessage,
} from "../../../../config/accounting";
import {
  fetchInvoices,
  getInvoice,
  updateInvoiceJson,
  updateInvoiceStatus,
  processInvoiceJson,
  createZohoInvoice,
  updateZohoInvoice,
  createManualInvoice,
  markInvoiceAsDuplicate,
  rejectInvoice,
  deletePostedInvoice,
  fetchInvoiceCounts,
} from "../api/invoicesApi";
import { InvoiceMapper } from "../mappers/InvoiceMapper";
import type { ApprovalAuditPayload } from "../../../../shared/utils/reviewWorkflow";

export class InvoiceRepositoryImpl implements InvoiceRepository {
  /**
   * Fetch all invoices with optional filtering
   */
  async fetchInvoices(params?: {
    limit?: number;
    offset?: number;
    status?: InvoiceStatus | InvoiceListStatus;
    reviewed?: InvoiceReviewStatus;
    search?: string;
    date_from?: string;
    date_to?: string;
    created_after?: string;
    created_before?: string;
    sort_by?:
      | "created_at"
      | "updated_at"
      | "filename"
      | "status"
      | "original_filename";
    sort_order?: "asc" | "desc";
  }): Promise<InvoicesResultEntity> {
    const response = await fetchInvoices(params);
    return InvoiceMapper.toInvoicesResultEntity(response);
  }

  /**
   * Get a single invoice by ID
   */
  async getInvoice(invoiceId: number): Promise<InvoiceEntity> {
    const invoiceDTO = await getInvoice(invoiceId);
    return InvoiceMapper.toInvoiceEntity(invoiceDTO);
  }

  /**
   * Update invoice JSON data
   */
  async updateInvoiceJson(
    invoiceId: number,
    invoiceData: InvoiceDataEntity
  ): Promise<InvoiceOperationResult> {
    try {
      // Convert Entity to DTO
      const invoiceDataDTO = InvoiceMapper.toInvoiceDataDTO(invoiceData);

      // Call API
      const invoiceDTO = await updateInvoiceJson(invoiceId, invoiceDataDTO);

      // Map back to entity and return operation result
      return {
        success: true,
        message: "Invoice updated successfully",
        invoice: InvoiceMapper.toInvoiceEntity(invoiceDTO),
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to update invoice",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Update invoice status
   */
  async updateInvoiceStatus(
    invoiceId: number,
    status: number
  ): Promise<InvoiceOperationResult> {
    try {
      await updateInvoiceStatus(invoiceId, status);
      return {
        success: true,
        message: "Invoice status updated successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to update invoice status",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Create invoice in Zoho Books
   */
  async createZohoInvoice(
    invoiceId: number,
    invoiceData: InvoiceDataEntity,
    pdfUrl: string,
    approvalAudit?: ApprovalAuditPayload,
  ): Promise<InvoiceOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      const invoiceDataDTO = InvoiceMapper.toInvoiceDataDTO(invoiceData);
      await createZohoInvoice(
        invoiceDataDTO,
        invoiceId.toString(),
        pdfUrl,
        approvalAudit,
      );

      // Fetch updated invoice
      // const invoiceDTO = await getInvoice(invoiceId);
      return {
        success: true,
        message: getCreatedInBackendSuccessMessage("Invoice", backend),
        // invoice: InvoiceMapper.toInvoiceEntity(invoiceDTO),
      };
    } catch (error: any) {
      const backend = getStoredAccountingBackend();
      return {
        success: false,
        message: getFailedCreateInBackendMessage("invoice", backend),
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Update invoice in Zoho Books
   */
  async updateZohoInvoice(
    invoiceId: number,
    invoiceData: InvoiceDataEntity,
    approvalAudit?: ApprovalAuditPayload,
  ): Promise<InvoiceOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      const invoiceDataDTO = InvoiceMapper.toInvoiceDataDTO(invoiceData);
      await updateZohoInvoice(
        invoiceId.toString(),
        invoiceDataDTO,
        approvalAudit,
      );

      // Fetch updated invoice
      // const invoiceDTO = await getInvoice(invoiceId);
      return {
        success: true,
        message: getUpdatedInBackendSuccessMessage("Invoice", backend),
        // invoice: InvoiceMapper.toInvoiceEntity(invoiceDTO),
      };
    } catch (error: any) {
      const backend = getStoredAccountingBackend();
      return {
        success: false,
        message: getFailedUpdateInBackendMessage("invoice", backend),
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Mark invoice as duplicate
   */
  async markInvoiceAsDuplicate(
    invoiceId: number
  ): Promise<InvoiceOperationResult> {
    try {
      await markInvoiceAsDuplicate(invoiceId);
      return {
        success: true,
        message: "Invoice marked as duplicate successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to mark invoice as duplicate",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Reject an invoice
   */
  async rejectInvoice(invoiceId: number): Promise<InvoiceOperationResult> {
    try {
      await rejectInvoice(invoiceId);
      return {
        success: true,
        message: "Invoice rejected successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to reject invoice",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Delete a posted invoice from Zoho and soft-delete locally
   */
  async deletePostedInvoice(
    invoiceId: number
  ): Promise<InvoiceOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      await deletePostedInvoice(invoiceId);
      return {
        success: true,
        message: getDeletedFromBackendSuccessMessage("Invoice", backend),
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to delete invoice",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Process invoice JSON for GL classification
   */
  async processInvoiceJson(
    invoiceData: InvoiceDataEntity
  ): Promise<InvoiceOperationResult> {
    try {
      // Convert Entity to DTO
      const invoiceDataDTO = InvoiceMapper.toInvoiceDataDTO(invoiceData);

      // Call API
      await processInvoiceJson(invoiceDataDTO);

      return {
        success: true,
        message: "Invoice processed successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to process invoice",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Fetch invoice counts for dashboard cards
   */
  async fetchInvoiceCounts(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<
    import("../../domain/entities/InvoiceEntity").InvoiceCountsEntity
  > {
    const response = await fetchInvoiceCounts(params);
    return {
      pendingCount: Number(response.pending_count) || 0,
      onHoldCount: Number(response.on_hold_count) || 0,
      underReviewCount: Number(response.under_review_count) || 0,
      postedCount: Number(response.posted_count) || 0,
      duplicateCount: Number(response.duplicate_count) || 0,
      rejectedCount: Number(response.rejected_count) || 0,
      rejectedDuplicateCount: Number(response.rejected_duplicate_count) || 0,
    };
  }

  /**
   * Create manual invoice (form data + optional attachments, then push to Zoho)
   */
  async createManualInvoice(
    invoiceData: InvoiceDataEntity,
    companyGstEnabled: number,
    attachments?: File[]
  ): Promise<ManualInvoiceCreatedEntity> {
    const invoiceDataDTO = InvoiceMapper.toInvoiceDataDTO(invoiceData);
    const response = await createManualInvoice(
      invoiceDataDTO,
      companyGstEnabled,
      attachments
    );
    const data = response.data;
    if (!data?.id) {
      throw new Error(response.message || "Create manual invoice failed");
    }
    return {
      id: data.id,
      zohoInvoiceId: data.zoho_invoice_id,
      invoiceNumber: data.invoice_number,
    };
  }
}
